import { Alert, Button, Group, NumberInput, SegmentedControl, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { DateInput, MonthPickerInput } from '@mantine/dates';
import { FormEvent, useMemo, useState } from 'react';
import { api, Catalog, Expense } from '../api';
import { isoDay, monthStart } from '../format';

const SHARED = 'shared';

export interface ExpenseDraft {
  op_date: string;
  amount: number | '';
  category_id: string | null;
  legal_entity_id: string; // id ООО, SHARED или '' — не выбрано
  department_id: string | null;
  accrual_month: string | null;
  payment_method: 'bank' | 'cash' | null;
  description: string;
  comment: string;
}

export function draftFrom(e: Expense | null, defaults: { departmentId: string | null; legalEntityId: string | null }, catalog: Catalog): ExpenseDraft {
  if (e) {
    return {
      op_date: e.op_date,
      amount: Number(e.amount),
      category_id: e.category_id,
      legal_entity_id: e.legal_entity_id ?? SHARED,
      department_id: e.department_id,
      accrual_month: e.accrual_month === monthStart(e.op_date) ? null : e.accrual_month,
      payment_method: e.payment_method,
      description: e.description ?? '',
      comment: e.comment ?? '',
    };
  }
  return {
    op_date: isoDay(new Date()),
    amount: '',
    category_id: null,
    // ООО без выбора по умолчанию, если у пользователя оно не одно: ошибиться молча нельзя
    legal_entity_id: defaults.legalEntityId ?? (catalog.legalEntities.length === 1 ? catalog.legalEntities[0].id : ''),
    department_id: defaults.departmentId,
    accrual_month: null,
    payment_method: 'bank',
    description: '',
    comment: '',
  };
}

interface Props {
  catalog: Catalog;
  initial: ExpenseDraft;
  expenseId: string | null;
  onSaved: (e: Expense) => void;
  onDelete?: () => void;
}

export function ExpenseForm({ catalog, initial, expenseId, onSaved, onDelete }: Props) {
  const [d, setD] = useState<ExpenseDraft>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof ExpenseDraft>(k: K, v: ExpenseDraft[K]) => setD((x) => ({ ...x, [k]: v }));

  const categories = useMemo(() => {
    const groups = new Map<string, { value: string; label: string }[]>();
    for (const c of catalog.categories) {
      const g = c.group_name ?? 'Прочее';
      groups.set(g, [...(groups.get(g) ?? []), { value: c.id, label: c.name }]);
    }
    return [...groups].map(([group, items]) => ({ group, items }));
  }, [catalog]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!d.legal_entity_id) {
      setError('Выберите, для какого ООО затрата, или «Общая»');
      return;
    }
    setBusy(true);
    setError(null);
    const body = {
      op_date: d.op_date,
      amount: d.amount === '' ? null : d.amount,
      category_id: d.category_id,
      legal_entity_id: d.legal_entity_id === SHARED ? null : d.legal_entity_id,
      department_id: d.department_id,
      accrual_month: d.accrual_month,
      payment_method: d.payment_method,
      description: d.description || null,
      comment: d.comment || null,
    };
    try {
      const saved = expenseId ? await api.patch<Expense>(`/expenses/${expenseId}`, body) : await api.post<Expense>('/expenses', body);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="md">
        {error && (
          <Alert color="red" variant="light" role="alert">
            {error}
          </Alert>
        )}
        <NumberInput
          label="Сумма"
          required
          min={0.01}
          decimalScale={2}
          thousandSeparator=" "
          decimalSeparator=","
          rightSection={<Text c="dimmed">₽</Text>}
          size="md"
          value={d.amount}
          onChange={(v) => set('amount', v === '' ? '' : Number(v))}
          inputMode="decimal"
        />
        <TextInput label="Назначение" placeholder="Что оплачено" value={d.description} onChange={(e) => set('description', e.currentTarget.value)} />
        <Select
          label="Статья"
          required
          searchable
          data={categories}
          value={d.category_id}
          onChange={(v) => set('category_id', v)}
          nothingFoundMessage="Нет такой статьи"
          comboboxProps={{ withinPortal: true }}
        />
        <DateInput
          label="Дата оплаты"
          required
          valueFormat="D MMMM YYYY"
          value={d.op_date}
          onChange={(v) => v && set('op_date', isoDay(v))}
        />
        <div>
          <Text size="sm" fw={500} mb={4}>
            Для какого ООО
          </Text>
          <SegmentedControl
            fullWidth
            value={d.legal_entity_id}
            onChange={(v) => set('legal_entity_id', v)}
            data={[...catalog.legalEntities.map((l) => ({ value: l.id, label: l.name.replace(/^Гинеклин /, '') })), { value: SHARED, label: 'Общая' }]}
          />
          {d.legal_entity_id === SHARED && (
            <Text size="xs" c="dimmed" mt={4}>
              Общая затрата делится между ООО по правилу (по умолчанию — по выручке месяца).
            </Text>
          )}
        </div>
        <Select
          label="Подразделение"
          data={catalog.departments.map((x) => ({ value: x.id, label: x.name }))}
          value={d.department_id}
          onChange={(v) => set('department_id', v)}
          clearable
        />
        <MonthPickerInput
          label="К какому месяцу относится"
          placeholder="Месяц оплаты"
          clearable
          valueFormat="MMMM YYYY"
          value={d.accrual_month}
          onChange={(v) => set('accrual_month', v ? monthStart(v) : null)}
        />
        <div>
          <Text size="sm" fw={500} mb={4}>
            Оплата
          </Text>
          <SegmentedControl
            fullWidth
            value={d.payment_method ?? 'none'}
            onChange={(v) => set('payment_method', v === 'none' ? null : (v as 'bank' | 'cash'))}
            data={[
              { value: 'bank', label: 'Банк' },
              { value: 'cash', label: 'Наличные' },
              { value: 'none', label: 'Не указано' },
            ]}
          />
        </div>
        <Textarea label="Комментарий" autosize minRows={1} value={d.comment} onChange={(e) => set('comment', e.currentTarget.value)} />
        <Group justify="space-between" mt="sm">
          {onDelete ? (
            <Button variant="subtle" color="red" onClick={onDelete}>
              Удалить
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" size="md" loading={busy}>
            Сохранить
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
